import type {
  AgentChat,
  AgentProcessSummary,
  AgentArtifact,
  SendMessagePayload,
  StreamChunk,
  MessagesPage
} from '../types'
import type { GetMessagesOptions } from './AgentTransport'
import type {
  Activity,
  AgentConfiguration,
  ActivitySummary,
  ActivitySummaryParams,
  AgentMessage,
  AgentToolCallStatus,
  DataWithCursor,
  MultiUserMessagePayload,
  MultiUserMessageResponse,
  SortOrder
} from '@multiplayer-app/ai-agent-types'
import { StreamChunkType } from '../types'
import type { ProxyTransportConfig } from '../../config/types'
import { HttpError } from '../../config/types'
import type { ModelOption } from '@multiplayer-app/ai-agent-types'
import type {
  AgentTransport,
  ActivityTreeNode,
  UpsertAgentPreferencesRequest,
  UpsertAgentPreferencesResponse,
  UpsertAgentPreferencesBatchRequest,
  UpsertAgentPreferencesBatchResponse
} from './AgentTransport'

const encoder = new TextEncoder()
const decoder = new TextDecoder()

/** Number of most-recent messages to fetch after streaming completes. Covers the new exchange. */
const POST_STREAM_MESSAGES_LIMIT = 3

export class ProxyTransport implements AgentTransport {
  private streamControllers = new Map<string, AbortController>()
  private socketId?: string

  constructor(private config: ProxyTransportConfig) {}

  updateConfig(next: ProxyTransportConfig) {
    if (next.mode !== 'proxy') {
      throw new Error('ProxyTransport.updateConfig requires proxy mode configuration')
    }
    this.config = next
  }

  /**
   * Set the Socket.IO socket ID to exclude from Socket.IO updates
   */
  setSocketId(socketId: string | undefined): void {
    this.socketId = socketId
  }

  private withHeaders(headers?: Record<string, string>, init?: RequestInit) {
    const base: Record<string, string> = {
      ...this.config.headers,
      ...headers
    }

    if (this.config.apiKey) {
      base['Authorization'] = `Bearer ${this.config.apiKey}`
    }

    // Only set JSON Content-Type when we actually send a body.
    // Fastify rejects empty JSON bodies if content-type is set.
    const hasBody = init?.body !== undefined && init?.body !== null
    const hasExplicitContentType = Object.keys(base).some((k) => k.toLowerCase() === 'content-type')
    if (hasBody && !hasExplicitContentType) {
      base['Content-Type'] = 'application/json'
    }

    return base
  }

  private async formatHttpError(path: string, response: Response): Promise<{ message: string; statusCode: number }> {
    const status = response.status
    const statusText = response.statusText
    const contentType = response.headers.get('content-type') ?? ''

    let detail: string | undefined
    try {
      if (contentType.includes('application/json')) {
        const json = (await response.json()) as any
        detail =
          (typeof json?.message === 'string' && json.message) ||
          (typeof json?.error === 'string' && json.error) ||
          (typeof json?.detail === 'string' && json.detail) ||
          (typeof json?.title === 'string' && json.title) ||
          (typeof json === 'string' && json) ||
          (json ? JSON.stringify(json) : undefined)
      } else {
        const text = (await response.text()).trim()
        // Avoid dumping HTML responses into the UI.
        const looksHtml = /<\s*html[\s>]|<\s*body[\s>]/i.test(text)
        if (!looksHtml && text) detail = text.length > 500 ? text.slice(0, 500) + '…' : text
      }
    } catch {
      // Ignore parse errors; fall back to statusText.
    }

    const base = `Message sending failed (${status}${statusText ? ` ${statusText}` : ''}).`
    return { message: detail ? `${base} ${detail}` : `${base} Please try again.`, statusCode: status }
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${this.config.baseUrl}${path}`, {
      ...init,
      credentials: 'include',
      headers: this.withHeaders(init?.headers as Record<string, string> | undefined, init),
      signal: init?.signal
    })

    if (!response.ok) {
      const { message, statusCode } = await this.formatHttpError(path, response)
      const error = new HttpError(message, statusCode)
      this.config.onError?.(error)
      throw error
    }

    // Common for DELETE endpoints: 204 No Content.
    if (response.status === 204) {
      return undefined as T
    }

    if (response.headers.get('content-type')?.includes('application/json')) {
      // Some endpoints may still respond with an empty body; treat as void.
      const text = await response.text()
      if (!text) return undefined as T
      return JSON.parse(text) as T
    }

    const text = await response.text()
    if (!text) return undefined as T
    return JSON.parse(text) as T
  }

  async sendMessage(payload: SendMessagePayload): Promise<AgentChat> {
    const headers: Record<string, string> = {}
    if (this.socketId) {
      headers['x-socket-id'] = this.socketId
    }
    return this.request<AgentChat>('/chats/send', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })
  }

  async streamMessage(
    payload: SendMessagePayload,
    onChunk: (chunk: StreamChunk) => void,
    options?: { streamKey?: string }
  ): Promise<{ id: string; messages: AgentMessage[] } | void> {
    const streamKey =
      options?.streamKey ?? payload.chatId ?? `stream-${Date.now()}-${Math.random().toString(16).slice(2)}`
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), this.config.timeoutMs ?? 5 * 60 * 1000) // 5 minutes

    try {
      const headers: Record<string, string> = { Accept: 'text/event-stream' }
      if (this.socketId) {
        headers['x-socket-id'] = this.socketId
      }

      if (controller.signal.aborted) {
        throw new Error('Stream was aborted before fetch started')
      }

      this.streamControllers.set(streamKey, controller)
      const response = await fetch(`${this.config.baseUrl}/chats/stream`, {
        method: 'POST',
        signal: controller.signal,
        credentials: 'include',
        body: JSON.stringify(payload),
        headers: this.withHeaders(headers, { method: 'POST', body: JSON.stringify(payload) })
      })

      if (!response.ok) {
        const { message, statusCode } = await this.formatHttpError('/chats/stream', response)
        const error = new HttpError(message, statusCode)
        this.config.onError?.(error)
        throw error
      }

      if (!response.body) {
        throw new Error('Streaming response body missing')
      }

      const reader = response.body.getReader()
      let buffer = ''
      let lastChatId: string | undefined
      let doneEmitted = false

      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const segments = buffer.split('\n\n')
        buffer = segments.pop() ?? ''

        for (const segment of segments) {
          const line = segment.trim()
          if (!line.startsWith('data:')) continue
          const payloadStr = line.replace(/^data:\s*/, '')
          if (!payloadStr || payloadStr === '[DONE]') {
            doneEmitted = true
            onChunk({ type: StreamChunkType.Message, done: true })
            continue
          }
          let json: StreamChunk
          try {
            json = JSON.parse(payloadStr) as StreamChunk
          } catch (error) {
            console.error('Failed to parse SSE chunk', error)
            continue
          }

          // Track chat ID from chat and message chunks
          if (json.type === StreamChunkType.Chat && json.chatId) {
            lastChatId = json.chatId
          }
          if (json.message?.chat) {
            lastChatId = json.message.chat
            // Allow cancelling the stream by chat id once the server assigns it.
            // Keep the original streamKey mapping as an alias (so Stop still works
            // if the UI is still keyed by the placeholder).
            this.streamControllers.set(lastChatId, controller)
          }
          onChunk(json)
        }
      }

      // The stream is over: finalize the UI immediately (commit pending assistant,
      // stop the streaming status) even if the [DONE] line was lost, and before the
      // post-stream messages fetch below adds its own latency.
      if (!doneEmitted) {
        onChunk({ type: StreamChunkType.Message, done: true })
      }

      // Fetch last messages after streaming. Metadata comes from the Chat chunk; we only need messages.
      if (lastChatId) {
        try {
          const page = await this.getMessages(lastChatId, { limit: POST_STREAM_MESSAGES_LIMIT })
          return { id: lastChatId, messages: page.messages }
        } catch (error) {
          console.error('Failed to fetch messages after streaming', error)
        }
      }
    } finally {
      clearTimeout(timeout)
      const keysToDelete: string[] = []
      for (const [key, value] of this.streamControllers.entries()) {
        if (value === controller) keysToDelete.push(key)
      }
      keysToDelete.forEach((key) => this.streamControllers.delete(key))
    }
    return undefined
  }

  async sendMultiUserMessage(payload: MultiUserMessagePayload): Promise<MultiUserMessageResponse> {
    const path = payload.chatId
      ? `/chats/${encodeURIComponent(payload.chatId)}/messages`
      : '/chats/multi-user/messages'
    return this.request<MultiUserMessageResponse>(path, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  cancelStream(streamKey?: string) {
    this.cancelHttpStream(streamKey)
    if (streamKey) {
      this.request(`/chats/${streamKey}/abort`, {
        method: 'POST',
        body: JSON.stringify({})
      }).catch((error) => {
        console.error('Failed to call abort API:', error)
      })
    }
  }

  cancelHttpStream(streamKey?: string) {
    if (streamKey) {
      const controller = this.streamControllers.get(streamKey)
      if (controller) {
        controller.abort()
        const keysToDelete: string[] = []
        for (const [key, value] of this.streamControllers.entries()) {
          if (value === controller) keysToDelete.push(key)
        }
        keysToDelete.forEach((key) => this.streamControllers.delete(key))
      }
      return
    }
    // Backwards compatible: cancel all streams if no key is provided.
    const controllers = new Set(this.streamControllers.values())
    for (const controller of controllers) {
      controller.abort()
    }
    this.streamControllers.clear()
  }

  async listChats(params?: {
    contextKey?: string
    skip?: number
    limit?: number
    userId?: string
    multiUser?: boolean
    sortField?: string
    sortOrder?: 'asc' | 'desc'
  }): Promise<DataWithCursor<AgentChat>> {
    const query = new URLSearchParams()
    if (params?.contextKey) query.set('contextKey', params.contextKey)
    if (params?.skip != null) query.set('skip', String(params.skip))
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.userId) query.set('userId', params.userId)
    if (params?.multiUser !== undefined) query.set('multiUser', String(params.multiUser))
    if (params?.sortField) query.set('sortField', params.sortField)
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder)
    const page = await this.request<DataWithCursor<AgentChat>>(`/chats?${query.toString()}`)
    return { cursor: page.cursor, data: page.data }
  }

  async getChat(chatId: string): Promise<AgentChat> {
    const chat = await this.request<AgentChat>(`/chats/${chatId}`)
    return chat
  }

  async getMessages(chatId: string, options?: GetMessagesOptions): Promise<MessagesPage> {
    const params = new URLSearchParams({})
    if (options?.limit != null) params.set('limit', String(options.limit))
    if (options?.before) params.set('before', options.before)
    return this.request<MessagesPage>(`/chats/${chatId}/messages?${params.toString()}`)
  }

  deleteChat(chatId: string): Promise<void> {
    return this.request(`/chats/${chatId}`, { method: 'DELETE' })
  }

  listAgents(): Promise<AgentProcessSummary[]> {
    return this.request('/agents')
  }

  controlAgent(options: { id: string; action: 'stop' | 'resume' | 'retry' }): Promise<AgentProcessSummary> {
    return this.request(`/agents/${options.id}/control`, {
      method: 'POST',
      body: JSON.stringify({ action: options.action })
    })
  }

  listArtifacts(chatId: string): Promise<AgentArtifact[]> {
    return this.request(`/chats/${chatId}/artifacts`)
  }

  listActivities(params?: {
    groupId?: string
    parentId?: string
    sourceType?: string
    skip?: number
    limit?: number
    sort?: { field: string; order: SortOrder }
    from?: string
    to?: string
    tenant?: Record<string, string> | { all?: boolean }
    ownerId?: string
  }): Promise<DataWithCursor<Activity>> {
    const query = new URLSearchParams()
    if (params?.groupId) query.set('groupId', params.groupId)
    if (params?.parentId) query.set('parentId', params.parentId)
    if (params?.sourceType) query.set('sourceType', params.sourceType)
    if (params?.skip != null) query.set('skip', String(params.skip))
    if (params?.limit != null) query.set('limit', String(params.limit))
    if (params?.sort != null) {
      query.set('sortField', params.sort.field)
      query.set('sortOrder', params.sort.order)
    }
    if (params?.from) query.set('from', params.from)
    if (params?.to) query.set('to', params.to)
    if (params?.tenant) query.set('tenant', JSON.stringify(params.tenant))
    if (params?.ownerId) query.set('ownerId', params.ownerId)
    return this.request<DataWithCursor<Activity>>(`/activities?${query.toString()}`)
  }

  listActivitySourceTypes(params?: {
    groupId?: string
    parentId?: string
    from?: string
    to?: string
    tenant?: Record<string, string> | { all?: boolean }
    ownerId?: string
  }): Promise<string[]> {
    const query = new URLSearchParams()
    if (params?.groupId) query.set('groupId', params.groupId)
    if (params?.parentId) query.set('parentId', params.parentId)
    if (params?.from) query.set('from', params.from)
    if (params?.to) query.set('to', params.to)
    if (params?.tenant) query.set('tenant', JSON.stringify(params.tenant))
    if (params?.ownerId) query.set('ownerId', params.ownerId)
    const suffix = query.toString()
    return this.request<string[]>(suffix ? `/activities/source-types?${suffix}` : '/activities/source-types')
  }

  async getActivity(activityId: string): Promise<ActivityTreeNode | null> {
    return this.request<ActivityTreeNode>(`/activities/${activityId}`)
  }

  async getActivitySummary(params?: ActivitySummaryParams): Promise<ActivitySummary[]> {
    const query = new URLSearchParams()
    if (params?.period?.from) {
      query.set('from', params.period.from instanceof Date ? params.period.from.toISOString() : params.period.from)
    }
    if (params?.period?.to) {
      query.set('to', params.period.to instanceof Date ? params.period.to.toISOString() : params.period.to)
    }
    if (params?.timeBucket) query.set('timeBucket', params.timeBucket)
    if (params?.model) query.set('model', params.model)
    if (params?.tenant) query.set('tenant', JSON.stringify(params.tenant))
    if (params?.ownerId) query.set('ownerId', params.ownerId)
    if (params?.groupBy?.length) query.set('groupBy', params.groupBy.join(','))
    const suffix = query.toString()
    return this.request<ActivitySummary[]>(suffix ? `/activities/summary?${suffix}` : '/activities/summary')
  }

  async recordToolCallAction(
    chatId: string,
    input: {
      toolCallId: string
      action: string
      data?: Record<string, unknown>
      systemMessage?: string
    },
    options?: { excludeSocketId?: boolean }
  ): Promise<Record<string, unknown> | void> {
    const headers: Record<string, string> = {}
    if (options?.excludeSocketId !== false && this.socketId) {
      headers['x-socket-id'] = this.socketId
    }
    return this.request(`/chats/${chatId}/actions`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        kind: 'toolCall',
        toolCallId: input.toolCallId,
        action: input.action,
        ...(input.data ? { data: input.data } : {}),
        ...(input.systemMessage ? { systemMessage: input.systemMessage } : {})
      })
    })
  }

  async updateToolCall(
    input: {
      chatId: string
      messageId: string
      toolCallId: string
      input?: Record<string, unknown>
      output?: Record<string, unknown>
      status?: AgentToolCallStatus
    },
    options?: { excludeSocketId?: boolean }
  ): Promise<AgentMessage> {
    const headers: Record<string, string> = {}
    if (options?.excludeSocketId !== false && this.socketId) {
      headers['x-socket-id'] = this.socketId
    }
    return this.request<AgentMessage>(
      `/chats/${input.chatId}/messages/${input.messageId}/tool-calls/${input.toolCallId}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          ...(input.input !== undefined ? { input: input.input } : {}),
          ...(input.output !== undefined ? { output: input.output } : {}),
          ...(input.status !== undefined ? { status: input.status } : {})
        })
      }
    )
  }

  async generateTitle(input: {
    content: string
    contextKey: string
    userId?: string
    strategy?: 'local' | 'llm'
  }): Promise<string> {
    return `${input.contextKey} request ${new Date().toISOString()}`
  }

  async listModels(): Promise<ModelOption[]> {
    const result = await this.request<{ models: ModelOption[] }>('/models')
    return result.models
  }

  async listAgentConfigurations(contextKey?: string): Promise<AgentConfiguration[]> {
    const query = new URLSearchParams()
    if (contextKey) {
      query.set('contextKey', contextKey)
    }
    const suffix = query.toString()
    const result = await this.request<{ agents: AgentConfiguration[] }>(
      suffix ? `/agents/config?${suffix}` : '/agents/config'
    )
    return result.agents
  }

  async upsertAgentPreferences(
    agentName: string,
    input: UpsertAgentPreferencesRequest
  ): Promise<UpsertAgentPreferencesResponse> {
    return this.request<UpsertAgentPreferencesResponse>(`/agents/${encodeURIComponent(agentName)}/preferences`, {
      method: 'POST',
      body: JSON.stringify(input)
    })
  }

  async upsertAgentPreferencesBatch(
    input: UpsertAgentPreferencesBatchRequest
  ): Promise<UpsertAgentPreferencesBatchResponse> {
    return this.request<UpsertAgentPreferencesBatchResponse>('/agents/preferences', {
      method: 'POST',
      body: JSON.stringify(input)
    })
  }
}
